import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import api from 'services/api';
import toastApi from 'services/toast';
import { getErrorMessage } from 'utils/errorHandler';
import { compareArrayOfObjects } from 'utils/arrayUtils';

import Button from 'components/button';
import PageTitleAndDescription from 'components/pageTitleAndDescription';
import TextAndInfo from 'components/textAndInfo';
import Badge from 'components/badge';
import Loader from 'components/loader';
import PreventExitModal from 'components/modal/preventExitModal';

import ColumnsList from './ColumnsList';

import {
  ColumnTypeText,
  SaveButtonWrapper,
  ListStyled,
  DividerLineStyled,
  StyledColForList,
  ModificationsText,
} from './styles';

const SearchReferencePage = () => {
  const { t } = useTranslation();

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const [hasModifications, setHasModifications] = useState(false);

  const [selectedColumns, setSelectedColumns] = useState([]);
  const [availableVehicleColumns, setAvailableVehicleColumns] = useState([]);
  const [availableProductColumns, setAvailableProductColumns] = useState([]);

  const originalSelectedColumns = useRef([]);

  useEffect(async () => {
    const columns = await api.loadColumnsReference();

    const selectedColumnsFromApi = columns.filter((column) => column.selected);
    const availableVehicleColumnsFromApi = columns.filter(
      (column) => !column.selected && column.fieldType === 'VEHICLE',
    );
    const availableProductColumnsFromApi = columns.filter(
      (column) => !column.selected && column.fieldType === 'PRODUCT',
    );

    originalSelectedColumns.current = selectedColumnsFromApi;
    setSelectedColumns(selectedColumnsFromApi);
    setAvailableVehicleColumns(availableVehicleColumnsFromApi);
    setAvailableProductColumns(availableProductColumnsFromApi);
    setLoadingPage(false);
  }, []);

  useEffect(() => {
    const areEquals = compareArrayOfObjects(selectedColumns, originalSelectedColumns.current);

    setHasModifications(!areEquals);
  }, [selectedColumns]);

  const selectAvailableColumns = (item) => {
    const newItem = { ...item, selected: true, order: selectedColumns.length + 1 };
    setAvailableVehicleColumns(
      availableVehicleColumns.filter((value) => value.field !== newItem.field),
    );
    setAvailableProductColumns(
      availableProductColumns.filter((value) => value.field !== newItem.field),
    );
    setSelectedColumns([...selectedColumns, newItem]);
  };

  const removeSelectedColumns = (item) => {
    if (selectedColumns.length === 1) {
      toastApi.warning(t('pages.searchReferencePage.atLeastOneSelectedColumn'));
      return;
    }
    const deletedItem = { ...item, selected: false };

    if (deletedItem.fieldType === 'VEHICLE') {
      setAvailableVehicleColumns([...availableVehicleColumns, deletedItem]);
    } else {
      setAvailableProductColumns([...availableProductColumns, deletedItem]);
    }

    const collumnsWithoutItem = selectedColumns.filter(
      (value) => value.field !== deletedItem.field,
    );

    setSelectedColumns(collumnsWithoutItem);
  };

  const save = async () => {
    const sortedColumns = selectedColumns.map(
      (column, index) => ({ ...column, order: index + 1 }),
    );
    await api.saveColumnsReference(sortedColumns);
    toastApi.success(
      t('pages.searchReferencePage.successSaveMessage'),
    );

    return sortedColumns;
  };

  const handleClickOnSave = async () => {
    try {
      setLoadingSave(true);
      const sortedColumns = await save();
      originalSelectedColumns.current = sortedColumns;
      setSelectedColumns(sortedColumns);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toastApi.error(errorMessage || error);
    } finally {
      setLoadingSave(false);
    }
  };

  const renderListContent = (item) => (
    <StyledColForList col={10}>
      <p>{item.name}</p>
      {!item.disableAggregator && <Badge text={t('pages.searchReferencePage.badgeCanBeAggregator')} variant="secondary" />}
    </StyledColForList>
  );

  const renderContent = () => (
    <>
      <ColumnsList
        columns={selectedColumns}
        setColumns={setSelectedColumns}
        onSelect={removeSelectedColumns}
      />

      <DividerLineStyled />

      <TextAndInfo
        text={t('pages.searchReferencePage.otherColumnsText')}
        info={t('pages.searchReferencePage.otherColumnsText2')}
      />

      <ColumnTypeText>
        {t('pages.searchReferencePage.aboutVehicle')}
      </ColumnTypeText>

      <ListStyled
        items={availableVehicleColumns}
        renderContent={renderListContent}
        selectLabel={t('common.select')}
        onSelect={selectAvailableColumns}
      />

      <ColumnTypeText>
        {t('pages.searchReferencePage.aboutProduct')}
      </ColumnTypeText>

      <ListStyled
        items={availableProductColumns}
        renderContent={renderListContent}
        selectLabel={t('common.select')}
        onSelect={selectAvailableColumns}
      />

      <SaveButtonWrapper>
        <ModificationsText>{t(hasModifications ? 'common.thereAreUnsavedChanges' : 'common.thereAreNoChangesToSave')}</ModificationsText>
        <Button
          disabled={!hasModifications}
          onClick={handleClickOnSave}
          loading={loadingSave}
          loadingLabel={t('common.saving')}
        >
          {t('common.save')}
        </Button>
      </SaveButtonWrapper>
    </>
  );

  return (
    <>
      <PreventExitModal cantExit={hasModifications} onClickPrimaryButton={save} />
      <PageTitleAndDescription title={t('pages.searchReferencePage.title')} description={t('pages.searchReferencePage.subtitle')} />
      {loadingPage ? <Loader withWrapper /> : renderContent()}
    </>
  );
};

export default SearchReferencePage;
